import {
  ForgotPasswordBody,
  ForgotPasswordConfirmBody,
  SignInBody,
  SignUpBody,
  SignUpConfirmBody,
  SignUpConfirmResendBody
} from '@shared/dto/auth.dto'
import { useMutation } from '@tanstack/react-query'
import { useAuthStore } from '../useAuthStore'
import axiosClient from '../useAxios'

export const useSignInMutation = () => {
  const { setAuthTokens } = useAuthStore()
  return useMutation({
    mutationKey: ['sign-in'],
    mutationFn: async (data: SignInBody) => {
      const response = await axiosClient.post('/auth/sign-in', {
        email: data.email,
        password: data.password
      })
      return {
        idToken: response.data.data.idToken,
        refreshToken: response.data.data.refreshToken,
        accessToken: response.data.data.accessToken
      }
    },
    onSuccess: data => {
      setAuthTokens(data)
    }
  })
}

export const useSignUpMutation = () => {
  return useMutation({
    mutationKey: ['sign-up'],
    mutationFn: async (data: SignUpBody) => {
      await axiosClient.post('/auth/sign-up', {
        email: data.email,
        password: data.password
      })
    }
  })
}

export const useSignUpConfirmMutation = () => {
  return useMutation({
    mutationKey: ['sign-up', 'confirm'],
    mutationFn: async (data: SignUpConfirmBody) => {
      await axiosClient.post('/auth/sign-up/confirm', {
        email: data.email,
        code: data.code
      })
    }
  })
}

export const useSignUpResendMutation = () => {
  return useMutation({
    mutationKey: ['sign-up', 'resend'],
    mutationFn: async (data: SignUpConfirmResendBody) => {
      await axiosClient.post('/auth/sign-up/resend', { email: data.email })
    }
  })
}

export const useForgotPasswordMutation = () => {
  return useMutation({
    mutationKey: ['forgot-password'],
    mutationFn: async (data: ForgotPasswordBody) => {
      await axiosClient.post('/auth/forgot-password', { email: data.email })
    }
  })
}

export const useForgotPasswordConfirmMutation = () => {
  return useMutation({
    mutationKey: ['forgot-password', 'confirm'],
    mutationFn: async (data: ForgotPasswordConfirmBody) => {
      await axiosClient.post('/auth/forgot-password/confirm', {
        email: data.email,
        code: data.code,
        newPassword: data.newPassword
      })
    }
  })
}

export const useChangePasswordMutation = () => {
  return useMutation({
    mutationKey: ['change-password'],
    mutationFn: async (data: {
      accessToken: string
      oldPassword: string
      newPassword: string
    }) => {
      await axiosClient.post('/auth/change-password', {
        accessToken: data.accessToken,
        oldPassword: data.oldPassword,
        newPassword: data.newPassword
      })
    }
  })
}

export const useSignOutMutation = () => {
  const { clearAuth } = useAuthStore()
  return useMutation({
    mutationKey: ['sign-out'],
    mutationFn: async (accessToken: string) => {
      await axiosClient.post('/auth/sign-out', {
        accessToken
      })
    },
    onSuccess: () => {
      clearAuth()
    }
  })
}
